import { type AxisSelection, AxisCode } from '@atlas/types'
import { CardShell, FieldShell, SectionCard } from '@atlas/ui-components'
import { Combobox, type ComboboxOption, Input, MultiCombobox } from '@atlas/ui-primitives'
import { RichTextEditor } from '@/components/ui/RichTextEditor'
import type { Campaign } from '@/domain/campaign.types'
import type { AssetTypeCode, ComplexityLevel } from '@/domain/work-order.types'
import type { RailAxis, RailValue } from '@/hooks/useLibrary'
import { FormCard } from './form-layout'
import {
	ATLAS_NOTES_HINT,
	ATLAS_NOTES_LABEL,
	axisOptions,
	brandOptionsFrom,
	CONTEXT_LABELS,
	campaignOptions,
	codedOptions,
	type FormErrors,
	facetedAxisOptions,
	findAxis,
	INDEPENDENT_CONTEXT_AXIS_CODES,
	LOCKED_NOTE,
	type LockedFields,
	NO_LOCKED_FIELDS,
	productOptionsFor,
	type WorkOrderFormState,
} from './form-model'

type Set = <K extends keyof WorkOrderFormState>(key: K, value: WorkOrderFormState[K]) => void

/** The title, in a card of its own at the top of the form. */
export function TitleCard({
	form,
	set,
	error,
	isLocked = false,
}: {
	form: WorkOrderFormState
	set: Set
	error?: string
	isLocked?: boolean
}) {
	return (
		<CardShell as="section" className="p-6">
			<FieldShell
				label="Title"
				htmlFor="title"
				span={3}
				required
				error={error}
				description={isLocked ? LOCKED_NOTE : undefined}
			>
				<Input
					id="title"
					disabled={isLocked}
					value={form.title}
					onChange={(e) => set('title', e.target.value)}
					placeholder="e.g. Bravecto Quantum launch — social kit"
					required
					aria-invalid={error ? true : undefined}
					aria-describedby={error ? 'title-error' : undefined}
				/>
			</FieldShell>
		</CardShell>
	)
}

/**
 * Classification card — complexity, asset-type and duration. The field list is
 * authored, NOT derived from the attribute-axis registry: seeding a new axis
 * must never grow this form, and the registry-driven version exposed internal
 * plumbing (order code, deliverable order, campaign code) to requesters.
 *
 * Duration is always optional; it is meaningful mainly for video deliverables,
 * but the form never gates it on the chosen asset type.
 */
export function ClassificationCard({
	form,
	set,
	axes,
	errors,
	locked = NO_LOCKED_FIELDS,
	isLoading,
}: {
	form: WorkOrderFormState
	set: Set
	axes: RailAxis[]
	errors: FormErrors
	/** Fields shown but not editable, each noted as changed in Workfront. */
	locked?: LockedFields
	/** The axis values are still loading. */
	isLoading?: boolean
}) {
	const note = (field: keyof WorkOrderFormState) => (locked.has(field) ? LOCKED_NOTE : undefined)
	return (
		<FormCard title="Classification">
			<FieldShell
				label="Complexity level"
				required
				error={errors.complexityLevel}
				description={note('complexityLevel')}
			>
				<Combobox
					aria-label="Complexity level"
					options={codedOptions(findAxis(axes, 'complexity_level'))}
					value={form.complexityLevel || null}
					onChange={(v) => set('complexityLevel', (v as ComplexityLevel) ?? '')}
					placeholder="Select a complexity…"
					isLoading={isLoading}
					isDisabled={locked.has('complexityLevel')}
					isRequired
					isInvalid={!!errors.complexityLevel}
				/>
			</FieldShell>
			<FieldShell label="Type of asset" required error={errors.assetTypeCode} description={note('assetTypeCode')}>
				<Combobox
					aria-label="Type of asset"
					options={codedOptions(findAxis(axes, 'asset_type_code'))}
					value={form.assetTypeCode || null}
					onChange={(v) => set('assetTypeCode', (v as AssetTypeCode) ?? '')}
					placeholder="Select an asset type…"
					isLoading={isLoading}
					isDisabled={locked.has('assetTypeCode')}
					isRequired
					isInvalid={!!errors.assetTypeCode}
				/>
			</FieldShell>
			<FieldShell label="Duration (seconds)" htmlFor="durationSeconds" error={errors.durationSeconds}>
				<Input
					id="durationSeconds"
					type="number"
					min={1}
					step={1}
					value={form.durationSeconds}
					onChange={(e) => set('durationSeconds', e.target.value)}
					placeholder="Optional, for video"
					aria-invalid={errors.durationSeconds ? true : undefined}
					aria-describedby={errors.durationSeconds ? 'durationSeconds-error' : undefined}
				/>
			</FieldShell>
		</FormCard>
	)
}

/**
 * One single-select context axis. The form holds one value per axis, but an order can hold
 * several (a Workfront order's audiences). Given more than one `storedCodes`, a locked axis
 * shows them all, read-only, and an editable one warns that picking a value replaces them.
 */
function ContextAxisField({
	axisCode,
	options,
	value,
	storedCodes,
	onChange,
	isLocked,
	placeholder,
	isLoading,
}: {
	axisCode: string
	options: ComboboxOption[]
	value: string | undefined
	storedCodes?: string[]
	onChange: (code: string | undefined) => void
	isLocked: boolean
	placeholder: string
	isLoading?: boolean
}) {
	const label = CONTEXT_LABELS[axisCode]
	const holdsSeveral = (storedCodes?.length ?? 0) > 1
	const note = isLocked
		? LOCKED_NOTE
		: holdsSeveral
			? `Holds ${storedCodes?.length} values: picking one replaces them all.`
			: undefined
	return (
		<FieldShell label={label} description={note}>
			{isLocked && storedCodes && holdsSeveral ? (
				<MultiCombobox aria-label={label} options={options} value={storedCodes} onChange={() => undefined} isDisabled />
			) : (
				<Combobox
					aria-label={label}
					options={options}
					value={value ?? null}
					onChange={onChange}
					isDisabled={isLocked}
					placeholder={placeholder}
					isLoading={isLoading}
				/>
			)}
		</FieldShell>
	)
}

/**
 * Context card. Region/Brand/Product cascade through the facet engine — they are
 * a genuine hierarchy. Language, Species, Audience and Channel do NOT: they are
 * served their full value list from the cached bootstrap, cost no facet request,
 * and are never cleared when a region or brand changes. Campaign is not an axis
 * at all but a picker of real Campaign entities, linked by campaign code.
 */
export function ContextCard({
	form,
	set,
	axes,
	campaigns,
	contextOptionsByAxis,
	isFacetFetching,
	isAxesLoading,
	locked = NO_LOCKED_FIELDS,
	storedTuple,
}: {
	form: WorkOrderFormState
	set: Set
	axes: RailAxis[]
	campaigns: Campaign[]
	contextOptionsByAxis: Record<string, RailValue[]>
	isFacetFetching?: boolean
	/** The axis registry's values are still loading. */
	isAxesLoading?: boolean
	/** Fields shown but not editable, each noted as changed in Workfront. */
	locked?: LockedFields
	/** The order's stored tuple, so an axis holding several values shows them or warns of them. */
	storedTuple?: AxisSelection
}) {
	const isTupleLocked = locked.has('tuple')
	const tupleNote = isTupleLocked ? LOCKED_NOTE : undefined
	const setTuple = (axisCode: string, code: string | undefined) => set('tuple', { ...form.tuple, [axisCode]: code })
	return (
		<FormCard title="Context">
			<ContextAxisField
				axisCode={AxisCode.GEOGRAPHY}
				options={facetedAxisOptions(contextOptionsByAxis[AxisCode.GEOGRAPHY])}
				value={form.tuple[AxisCode.GEOGRAPHY]}
				storedCodes={storedTuple?.[AxisCode.GEOGRAPHY]}
				onChange={(v) => setTuple(AxisCode.GEOGRAPHY, v)}
				isLocked={isTupleLocked}
				placeholder={isFacetFetching ? 'Loading…' : 'Any region / country…'}
				isLoading={isFacetFetching}
			/>
			{/* From the REGISTRY, not the facet: a facet answers "what has been classified",
			    which is the wrong question when creating an order — it would offer only the
			    combinations someone already used. Roots only; the subtree is the field below. */}
			<FieldShell label={CONTEXT_LABELS.brand} description={tupleNote}>
				<Combobox
					aria-label={CONTEXT_LABELS.brand}
					options={brandOptionsFrom(findAxis(axes, AxisCode.BRAND))}
					value={form.tuple[AxisCode.BRAND] ?? null}
					onChange={(v) => {
						// A product belongs to its brand, so it cannot outlive a change of brand.
						set('tuple', { ...form.tuple, [AxisCode.BRAND]: v, product: undefined })
					}}
					isDisabled={isTupleLocked}
					placeholder="Any brand…"
					isLoading={isAxesLoading}
				/>
			</FieldShell>
			{/* A second view of the SAME axis, not one of its own: brand, family and product
			    are one merged hierarchy, so the product list is the selected brand's subtree. */}
			<FieldShell label={CONTEXT_LABELS.product} description={tupleNote}>
				<Combobox
					aria-label={CONTEXT_LABELS.product}
					options={productOptionsFor(findAxis(axes, AxisCode.BRAND), form.tuple[AxisCode.BRAND])}
					value={form.tuple.product ?? null}
					onChange={(v) => setTuple('product', v)}
					isDisabled={isTupleLocked || !form.tuple[AxisCode.BRAND]}
					placeholder={form.tuple[AxisCode.BRAND] ? 'Any product…' : 'Pick a brand first…'}
					isLoading={isAxesLoading}
				/>
			</FieldShell>
			{INDEPENDENT_CONTEXT_AXIS_CODES.map((code) => (
				<ContextAxisField
					key={code}
					axisCode={code}
					options={axisOptions(findAxis(axes, code))}
					value={form.tuple[code]}
					storedCodes={storedTuple?.[code]}
					onChange={(v) => setTuple(code, v)}
					isLocked={isTupleLocked}
					placeholder={`Any ${CONTEXT_LABELS[code].toLowerCase()}…`}
					isLoading={isAxesLoading}
				/>
			))}
			<FieldShell label="Campaign" description={locked.has('campaignCode') ? LOCKED_NOTE : undefined}>
				<Combobox
					aria-label="Campaign"
					options={campaignOptions(campaigns)}
					value={form.campaignCode || null}
					onChange={(v) => set('campaignCode', v ?? '')}
					isDisabled={locked.has('campaignCode')}
					placeholder="No campaign…"
				/>
			</FieldShell>
		</FormCard>
	)
}

/**
 * Briefing card: rich-text description (WYSIWYG -> sanitized HTML). It is always ATLAS's own:
 * on a Workfront order it is titled "ATLAS notes", with a hint that Workfront never changes it,
 * because Workfront's description lands in the external description instead.
 */
export function BriefingCard({
	form,
	set,
	isWorkfront = false,
}: {
	form: WorkOrderFormState
	set: Set
	isWorkfront?: boolean
}) {
	const title = isWorkfront ? ATLAS_NOTES_LABEL : 'Description'
	return (
		<SectionCard title={title} className="p-6" description={isWorkfront ? ATLAS_NOTES_HINT : undefined}>
			<RichTextEditor
				aria-label={title}
				value={form.briefing}
				onChange={(html) => set('briefing', html)}
				placeholder="Describe the deliverable, versions and mandatory elements…"
			/>
		</SectionCard>
	)
}
